// Reducer for responses, includes in-memory cache per event
// NOTE: We define our own defaultState here instead of relying on an empty initialState import.
//       This keeps existing imports intact while giving us typed defaults.
import initialState from './initialState';
import {
  FETCH_NOTEWORTHY_ERROR,
  FETCH_NOTEWORTHY_SUCCESS,
  FETCH_RESPONSES,
  FETCH_RESPONSES_DONE,
  FETCH_RESPONSES_ERROR,
  FETCH_RESPONSES_LOADING,
  FETCH_RESPONSES_SUCCESS,
  UPDATE_NOTEWORTHY_RESPONSES,
  UPDATE_NOTEWORTHY_RESPONSES_DONE,
  UPDATE_NOTEWORTHY_RESPONSES_ERROR,
  UPDATE_NOTEWORTHY_RESPONSES_LOADING,
  UPDATE_NOTEWORTHY_RESPONSES_SUCCESS
} from '../actions/actionTypes';
import { USE_CACHED_RESPONSES, STORE_RESPONSES_CACHE } from '../actions/actionTypes';

// -----------------------------------------------------------------------------
// Default state – extended to include an in-memory cache keyed by eventId
// -----------------------------------------------------------------------------
const defaultState = {
  loading: false,
  error: null,
  responses: null,
  noteworthy: null,
  noteworthyError: null,
  cache: {}
};

const normalizeNoteworthy = (payload) => {
  if (!payload) {
    return [];
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload.noteworthy_posts)) {
    return payload.noteworthy_posts;
  }

  if (payload.noteworthy_posts && Array.isArray(payload.noteworthy_posts.posts)) {
    return payload.noteworthy_posts.posts;
  }

  if (Array.isArray(payload.posts)) {
    return payload.posts;
  }

  return [];
};

// -----------------------------------------------------------------------------
// Reducer implementations
// -----------------------------------------------------------------------------
const reducerFetchResponses = (state = defaultState, action) => {
  switch (action.type) {
    case FETCH_RESPONSES_LOADING:
      return {
        ...state,
        loading: true,
        error: null,
        responses: null
      };
    case USE_CACHED_RESPONSES:
      // console.log('responsesReducer: Using cached responses for event', action.eventId);
      return {
        ...state,
        loading: false,
        error: null,
        responses: (action.data.posts && action.data.posts.posts) ? action.data.posts.posts : action.data.posts, // ensure array
        noteworthy: normalizeNoteworthy(action.data.noteworthy_posts || action.data)
      };
    case STORE_RESPONSES_CACHE:
      // console.log('responsesReducer: Storing cache for event', action.eventId, 'at', action.fetchedAt);
      return {
        ...state,
        cache: {
          ...state.cache,
          [action.eventId]: {
            data: action.data,
            fetchedAt: action.fetchedAt
          }
        }
      };
    case FETCH_RESPONSES_SUCCESS:
      return {
        ...state,
        error: null,
        responses: action.responses.posts
      };
    case FETCH_RESPONSES_ERROR:
      return {
        ...state,
        error: action.error,
        responses: null
      };
    case FETCH_RESPONSES_DONE:
      return {
        ...state,
        loading: false
      };
    default:
      return state;
  }
};

const reducerFetchNoteworthy = (state = defaultState, action) => {
  switch (action.type) {
    case FETCH_NOTEWORTHY_SUCCESS:
      return {
        ...state,
        noteworthyError: null,
        noteworthy: normalizeNoteworthy(action.responses)
      };
    case FETCH_NOTEWORTHY_ERROR:
      return {
        ...state,
        noteworthyError: action.error,
        noteworthy: null
      };
    default:
      return state;
  }
};

const reducerUpdateNoteworthyResponses = (state = defaultState, action) => {
  switch (action.type) {
    case UPDATE_NOTEWORTHY_RESPONSES_LOADING:
      return {
        ...state,
        loading: true,
        noteworthyError: null,
        noteworthy: null
      };
    case UPDATE_NOTEWORTHY_RESPONSES_SUCCESS:
      return {
        ...state,
        noteworthyError: null,
        noteworthy: normalizeNoteworthy(action.responses)
      };
    case UPDATE_NOTEWORTHY_RESPONSES_ERROR:
      return {
        ...state,
        noteworthyError: action.error,
        noteworthy: null
      };
    case UPDATE_NOTEWORTHY_RESPONSES_DONE:
      return {
        ...state,
        loading: false
      };
    default:
      return state;
  }
};

export default (state = initialState, action) => {
  if (action.type.startsWith(FETCH_RESPONSES)) {
    return reducerFetchResponses(state, action);
  } else if (action.type === STORE_RESPONSES_CACHE || action.type === USE_CACHED_RESPONSES) {
    // Handle cache actions via fetchResponses reducer logic
    return reducerFetchResponses(state, action);
  } else if (action.type.startsWith(UPDATE_NOTEWORTHY_RESPONSES)) {
    return reducerUpdateNoteworthyResponses(state, action);
  } else {
    return reducerFetchNoteworthy(state, action);
  }
};
